import { ColorPopover } from '../../../components/Popover/ColorPopover';
import { Annotation, Color } from '../../../types/graphqlTypes';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useUserDefaultColor } from '../../Properties/integration/UserSettingsIntegration';
import { getAnnotationColorProperty } from '@pointorama/potree-shared';
import { colorToHex, hexToColor } from '../../../utils/colorUtils';

const EditAnnotationColors_: React.FC2<{ annotations: Annotation[]; projectId: string }> = ({ annotations, projectId }) => {
  const { colorObject: defaultObjectColor } = useUserDefaultColor();

  const firstAnnotation = annotations[0];

  // Local state synced with reactive property
  const [currentColor, setCurrentColor] = useState<Color>(() => {
    if (!firstAnnotation) return defaultObjectColor;
    try {
      const hex = getAnnotationColorProperty(firstAnnotation.identifier).value as string;
      return hex ? hexToColor(hex) : defaultObjectColor;
    } catch {
      return defaultObjectColor;
    }
  });

  // Subscribe to reactive property + sync initial value when annotation changes
  useEffect(() => {
    if (!firstAnnotation) return;
    try {
      const property = getAnnotationColorProperty(firstAnnotation.identifier);
      // `subscribe` (unlike `subscribeToChanges`) fires immediately with the current value, so
      // this one call both seeds the initial colour and tracks later remote/Potree updates —
      // no separate read-and-set before subscribing.
      const sub = property.subscribe((newValue) => {
        const newHex = newValue as string;
        if (newHex) setCurrentColor(hexToColor(newHex));
      });
      return () => sub.unsubscribe();
    } catch {
      // Property service not initialized yet
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on identifier, not object reference
  }, [firstAnnotation?.identifier]);

  // For multi-select: check if colors are mixed
  const displayColor = useMemo(() => {
    if (annotations.length <= 1) return currentColor;
    try {
      const gray = { r: 150, g: 150, b: 150, a: 1 };
      const firstHex = getAnnotationColorProperty(annotations[0].identifier).value as string;
      for (let i = 1; i < annotations.length; i++) {
        const hex = getAnnotationColorProperty(annotations[i].identifier).value as string;
        if (hex !== firstHex) return gray;
      }
    } catch {
      // Property service not initialized yet
    }
    return currentColor;
  }, [annotations, currentColor]);

  const onEditAnnotationColor = useCallback(
    (color: Color) => {
      if (annotations.length === 0) return;

      const hexColor = colorToHex(color);
      try {
        for (const annotation of annotations) {
          getAnnotationColorProperty(annotation.identifier).value = hexColor;
        }
      } catch {
        // Property service not initialized yet
      }
      setCurrentColor(color);
    },
    [annotations],
  );

  if (annotations.length === 0) return null;

  return (
    <div className="flex items-center w-full">
      <ColorPopover wide value={displayColor} onChange={onEditAnnotationColor} />
    </div>
  );
};

export const EditAnnotationColors = memo(EditAnnotationColors_);
